/**
 * Registry of which on-screen tables have actually hydrated their felt.
 *
 * A table is "hydrated" once its first table snapshot (gotTexas/gotOmaha) has
 * landed — i.e. `table.game.tabletype` is set and the felt shows real state
 * instead of the loading spinner. Each mounted <Table> reports itself here (see
 * components/Table/index.tsx).
 *
 * ConnectionManager's foreground felt-staleness gate reads this to decide
 * whether a reconnect is actually warranted. The gate used to reconnect on pure
 * push-silence (no gotTexas within its window), but a perfectly healthy table
 * between hands emits no gotTexas for many seconds (only gotTnmStatus ticks), so
 * that misread "quiet" as "stuck" and reconnected a live table — tearing down
 * the sockets, blanking the felt, and popping the "Exit Table" modal. Gating the
 * reconnect on `hasUnhydratedTable()` means we only escalate to a reconnect when
 * a table the player is looking at genuinely never hydrated (the RCA 20260713
 * item-C blank-felt / "spinning of death" case), never a live-but-quiet one.
 *
 * Read imperatively (no React re-render / context plumbing) at the single gate
 * decision point.
 */

// gameIDs of every <Table> currently mounted (single, grid cell, or mobile).
const mounted = new Set<string>();
// Subset of `mounted` whose felt has hydrated (tabletype present).
const hydrated = new Set<string>();

const key = (gameID: string | number): string => String(gameID);

/** A <Table> mounted — start tracking it (un-hydrated until proven otherwise). */
export function registerTable(gameID: string | number): void {
  mounted.add(key(gameID));
}

/** A <Table> unmounted — stop tracking it. */
export function unregisterTable(gameID: string | number): void {
  const k = key(gameID);
  mounted.delete(k);
  hydrated.delete(k);
}

/** Report a mounted table's current hydration (felt state present or not). */
export function setTableHydrated(gameID: string | number, isHydrated: boolean): void {
  const k = key(gameID);
  if (isHydrated) hydrated.add(k);
  else hydrated.delete(k);
}

/**
 * True when at least one mounted table has NOT hydrated its felt — the only
 * condition under which a felt-staleness reconnect is warranted.
 */
export function hasUnhydratedTable(): boolean {
  return Array.from(mounted).some((gameID) => !hydrated.has(gameID));
}

/** Test-only reset of the module-level registries. */
export function __resetTableHydration(): void {
  mounted.clear();
  hydrated.clear();
}
